import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Options<E, I extends number | string> = {
	/** The id the URL carries, read from `useUrlState`. */
	id: I | null;
	/** Writes that id to the URL. */
	setId: (value: I) => void;
	/** Removes it from the URL, without writing anything else. */
	dropId: () => void;
	/** The records the list currently holds. */
	rows: readonly E[];
	/**
	 * True only when `rows` belongs to the filters in the URL right now. A query whose key
	 * changed hands back an empty list while it refetches, and taking that for an answer
	 * would drop a selection that is about to come back.
	 */
	ready: boolean;
	getId: (row: E) => I | null | undefined;
};

/**
 * Master/detail selection kept in the URL.
 *
 * The id travels; the record is resolved against the rows the server returned, so a link
 * can never carry a copy of a row that has since changed. A record being added has no id
 * yet, so it lives here instead of in the URL, and it stays on screen until the refetched
 * list carries it.
 *
 * Written for the Configuration catalogs (#717), which all share this shape, after the
 * same twenty-five lines were spelled out by hand on eight pages in #716.
 */
export const useUrlSelection = <E, I extends number | string = number>({
	id,
	setId,
	dropId,
	rows,
	ready,
	getId,
}: Options<E, I>) => {
	// The record being added, or the one just saved while the list catches up.
	const [draft, setDraft] = useState<E | null>(null);

	// The callers pass inline arrows, so the effects below read them through refs and do
	// not re-run on every render.
	const getIdRef = useRef(getId);
	getIdRef.current = getId;
	const setIdRef = useRef(setId);
	setIdRef.current = setId;
	const dropIdRef = useRef(dropId);
	dropIdRef.current = dropId;

	const sameId = (row: E, value: I) =>
		String(getIdRef.current(row)) === String(value);

	const current = useMemo(() => {
		if (draft) return draft;
		if (id === null) return null;
		return rows.find((row) => sameId(row, id)) ?? null;
	}, [draft, rows, id]);

	const isNew = useMemo(() => {
		if (!draft) return false;
		const draftId = getIdRef.current(draft);
		return draftId === null || draftId === undefined || Number(draftId) <= 0;
	}, [draft]);

	// A link to a record the list does not hold would leave the panel empty, so the key goes.
	useEffect(() => {
		if (!ready || id === null || draft) return;
		if (!rows.some((row) => sameId(row, id))) dropIdRef.current();
	}, [ready, rows, id, draft]);

	// The record just saved stays on screen until the refetched list carries it.
	useEffect(() => {
		if (!draft) return;
		const draftId = getIdRef.current(draft);
		if (draftId === null || draftId === undefined || Number(draftId) <= 0)
			return;
		if (rows.some((row) => sameId(row, draftId))) setDraft(null);
	}, [rows, draft]);

	const select = useCallback((row: E) => {
		const rowId = getIdRef.current(row);
		if (rowId === null || rowId === undefined) return;
		setDraft(null);
		setIdRef.current(rowId);
	}, []);

	/** Starts adding a record: it has no id, so nothing is written to the URL. */
	const startNew = useCallback((blank: E) => {
		setDraft(blank);
		dropIdRef.current();
	}, []);

	const clear = useCallback(() => {
		setDraft(null);
		dropIdRef.current();
	}, []);

	/** After a save: point the URL at the record and hold it until the list brings it. */
	const adopt = useCallback((row: E) => {
		const rowId = getIdRef.current(row);
		setDraft(row);
		if (rowId !== null && rowId !== undefined) setIdRef.current(rowId);
	}, []);

	return { current, isNew, select, startNew, clear, adopt };
};
